<template>
  <div class="form-grid">
    <!-- Left Column -->
    <div class="form-column">
      <label>
        Enter Email (Same As Registered In MyWhoosh)
        <input :value="formData.binance_email" type="email" placeholder="Enter Your Email Used In Binance" required
          @input="$emit('update:binance_email', $event.target.value)" />
        <span v-if="errors.binance_email" class="error-message">
          {{ errors.binance_email }}
        </span>
      </label>

      <label>
        Phone number
        <div class="phone-input-group">
          <select :value="selectedCountry" class="country-select"
            @change="$emit('country-changed', $event.target.value)">
            <option v-for="country in countries" :key="country.code" :value="country.code">
              {{ country.emoji }} +{{ country.dialCode }}
            </option>
          </select>
          <input :value="nationalPhoneNumber" type="tel" placeholder="Phone number"
            @input="$emit('phone-input', $event.target.value)" class="phone-input" :class="{
              'is-invalid': phoneInputTouched && !isPhoneValid,
              'is-valid': phoneInputTouched && isPhoneValid,
            }" required />
        </div>
        <span v-if="errors.phone_number" class="error-message">
          {{ errors.phone_number }}
        </span>
        <small v-if="phoneInputTouched && !isPhoneValid && !errors.phone_number" class="text-danger">
          Please enter a valid phone number
        </small>
        <small v-if="phoneInputTouched && isPhoneValid" class="text-success">
          Valid phone number format
        </small>
      </label>

      <label>
        Binance public wallet address / Id
        <input :value="formData.wallet_address" type="text" placeholder="Wallet Address Or Id" required
          @input="$emit('update:wallet_address', $event.target.value)" />
        <span v-if="errors.wallet_address" class="error-message">
          {{ errors.wallet_address }}
        </span>
      </label>

      <label class="otp-wrapper">
        Phone OTP code
        <input name="otp" :value="formData.phone_otp" type="text" placeholder="Enter OTP Code" required
          @input="$emit('update:phone_otp', $event.target.value)" />
        <button type="button" class="otp-btn" @click="$emit('request-phone-otp')"
          :disabled="isPhoneOTPRequested || !isPhoneValid">
          {{ phoneOTPButtonText }}
        </button>
        <span v-if="errors.phone_otp" class="error-message">
          {{ errors.phone_otp }}
        </span>
      </label>

      <label class="otp-wrapper">
        Email OTP code
        <input name="otp" :value="formData.email_otp" type="text" placeholder="Enter OTP Code" required
          @input="$emit('update:email_otp', $event.target.value)" />
        <button type="button" class="otp-btn" @click="$emit('request-email-otp')" :disabled="isEmailOTPRequested">
          {{ emailOTPButtonText }}
        </button>
        <span v-if="errors.email_otp" class="error-message">
          {{ errors.email_otp }}
        </span>
      </label>
    </div>

    <!-- Right Column -->
    <div class="form-column">
      <label>
        Passport Proof
        <small class="upload-info">Maximum file size: 5MB (JPEG, PNG, JPG)</small>
        <div class="custom-upload">
          <input type="file" id="passportProof" @change="handlePassportUpload" accept="image/*" />
          <div class="upload-box">
            <svg class="upload-icon" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
              fill="currentColor">
              <path d="M5 20h14v-2H5v2zm7-18L5.33 11h4.17v4h4.99v-4h4.17L12 2z" />
            </svg>
            <p>
              {{ passportFileName || "Click to upload or drag & drop" }}
            </p>
          </div>
        </div>
        <span v-if="errors.passport_proof" class="error-message">
          {{ errors.passport_proof }}
        </span>
      </label>

      <label>
        Binance account verification center screenshot
        <small class="upload-info">Maximum 5 files, 5MB each</small>
        <div class="custom-upload">
          <input type="file" id="binanceScreenshot" @change="handleBinanceScreenshotUpload" accept="image/*" multiple />
          <div class="upload-box">
            <svg class="upload-icon" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
              fill="currentColor">
              <path d="M5 20h14v-2H5v2zm7-18L5.33 11h4.17v4h4.99v-4h4.17L12 2z" />
            </svg>
            <p>
              {{
                binanceScreenshotFileNames.length > 0
                  ? `${binanceScreenshotFileNames.length} file(s) selected`
                  : "Click to upload or drag & drop multiple files"
              }}
            </p>
          </div>
        </div>

        <!-- General binance_screenshot error display -->
        <span v-if="errors.binance_screenshot" class="error-message">
          {{ errors.binance_screenshot }}
        </span>
      </label>

      <div v-if="binanceScreenshotFileNames.length > 0" class="selected-files">
        <div v-for="(fileName, index) in binanceScreenshotFileNames" :key="index" class="file-item"
          :class="{ 'has-error': getFileError(index) }">
          <div class="file-content">
            <span class="file-name">{{ fileName }}</span>
            <!-- Show individual file error under filename -->
            <div v-if="getFileError(index)" class="file-error">
              {{ getFileError(index) }}
            </div>
          </div>
          <button type="button" @click="removeBinanceFile(index)" class="remove-file-btn">
            ×
          </button>
        </div>
      </div>

      <!-- Terms and Conditions Section -->
      <div class="terms-checkbox">
        <input type="checkbox" :checked="formData.terms_accepted"
          @change="$emit('update:terms_accepted', $event.target.checked)" required />
        <span>
          I agree to the
          <a href="https://www.mywhoosh.com/terms-and-conditions" target="_blank">Terms and Conditions</a>
          and
          <a href="https://www.mywhoosh.com/privacy-policy/" target="_blank">Privacy Policy</a>
        </span>
      </div>
      <div v-if="errors.terms_accepted" class="error-message">
        {{ errors.terms_accepted }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VerificationForm",
  props: {
    formData: {
      type: Object,
      required: true,
    },
    selectedCountry: {
      type: String,
      required: true,
    },
    nationalPhoneNumber: {
      type: String,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
    isPhoneValid: {
      type: Boolean,
      required: true,
    },
    phoneInputTouched: {
      type: Boolean,
      required: true,
    },
    isPhoneOTPRequested: {
      type: Boolean,
      required: true,
    },
    isEmailOTPRequested: {
      type: Boolean,
      required: true,
    },
    phoneOTPButtonText: {
      type: String,
      required: true,
    },
    emailOTPButtonText: {
      type: String,
      required: true,
    },
    passportFileName: {
      type: String,
      required: true,
    },
    binanceScreenshotFileNames: {
      type: Array,
      required: true,
    },
  },
  computed: {
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
  },
  methods: {
    getFileError(index) {
      const errorKey = `binance_screenshot.${index}`;
      return this.errors[errorKey] || null;
    },
    handlePassportUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.$emit("passport-uploaded", file);
      }
    },
    removeBinanceFile(index) {
      this.$emit("remove-binance-file", index);
    },
    handleBinanceScreenshotUpload(event) {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        this.$emit("binance-screenshots-uploaded", files);
      }
    },
  },
};
</script>
<style scoped>
.file-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.file-name {
  margin-bottom: 2px;
}

.file-item.has-error {
  border-color: #dc3545;
  background-color: #fff5f5;
}

.file-error {
  color: #dc3545;
  font-size: 12px;
  font-weight: 500;
  margin-top: 4px;
}

.remove-file-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 18px;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  /* Prevent button from shrinking */
}

.remove-file-btn:hover {
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
}

.terms-section {
  width: 100%;
  margin: 30px 0;
  padding: 20px;
  border: 2px solid #f0f0f0;
  border-radius: 10px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.terms-section:hover {
  border-color: #fcb913;
  box-shadow: 0 4px 12px rgba(252, 185, 19, 0.1);
}

.terms-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 15px;
  text-align: left;
  line-height: 1.4;
  font-weight: 500;
  color: #333;
}

.terms-checkbox input[type="checkbox"] {
  margin: 0;
  width: 18px;
  height: 18px;
  accent-color: #fcb913;
  cursor: pointer;
}

.terms-checkbox span {
  display: inline;
  flex: 1;
}

.terms-checkbox a {
  color: #fcb913;
  text-decoration: none;
  font-weight: 600;
  border-bottom: 1px solid transparent;
  transition: all 0.3s ease;
}

.terms-checkbox a:hover {
  color: #e6a700;
  border-bottom-color: #e6a700;
}

.error-message {
  display: block;
  color: #dc3545;
  font-size: 13px;
  margin-top: 10px;
  text-align: left;
  font-weight: 500;
  padding: 8px 12px;
  background-color: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 6px;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .terms-section {
    margin: 20px 0;
    padding: 16px;
  }

  .terms-checkbox {
    font-size: 14px;
  }
}

.selected-files {
  margin-top: 10px;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  /* Changed from center to flex-start */
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 5px;
  font-size: 14px;
}

.upload-info {
  display: block;
  color: #6c757d;
  font-size: 12px;
  margin-bottom: 8px;
  font-weight: normal;
}
</style>
